import { computed } from 'vue'
import { getOnNavigation } from '@/common/utils/event-list/get-on-navigation'
import { getOnClick } from '@/common/utils/event-list/get-on-click'
import { getContentType } from '@/common/utils/cell/get-content-type'
import { shaveUrl } from '@/common/utils/cell/shave-url'
import { useRouterComposable } from '@/common/use/useRouterComposable'
import { setGoDetailUrl } from '@/common/utils/cell/set-go-detail-url'
import { changeStreamUrl } from '@/common/utils/cell/change-stream-url'
import { sendClickEventLog } from '@/common/utils/cell/send-click-event-log'
import { EventBus } from '@/common/utils/event-bus'

/**
 * useCell from cell mixin
 * 구형 cell mixin 을 대응하는 use 메서드
 * @param cellInfo
 */
export const useCell = (cellInfo = {}) => {
  const { pushRouter } = useRouterComposable()

  const ageTag = computed(() => cellInfo.age_tag ?? '')
  const alt = computed(() => cellInfo.alt ?? '')
  const bgColor = computed(() => cellInfo.bgColor ?? '')
  const brandTagList = computed(() => cellInfo.brand_taglist ?? [])
  const buttonText = computed(() => cellInfo.buttonText ?? '')
  const eventList = computed(() => cellInfo.event_list ?? [])
  const logoImage = computed(() => cellInfo.logoImage ?? '')
  const logoTitle = computed(() => cellInfo.logoTitle ?? '')
  const progress = computed(() => Number(cellInfo.progress ?? '0'))
  const thumbnail = computed(() => cellInfo.thumbnail)
  const rank = computed(() => cellInfo.rank ?? '')
  const ratings = computed(() => cellInfo.ratings ?? '')
  const targetAge = computed(() => Number(cellInfo.age ?? 0))
  const titleList = computed(() => cellInfo.title_list ?? [])
  const topTagList = computed(() => [
    ...(cellInfo.top_taglist || []),
    ...(cellInfo.bottom_taglist || []),
  ])
  const altTitle = computed(
    () =>
      cellInfo.alt ??
      titleList.value
        .map((el) => el.text)
        .join(' ')
        .trim(),
  )
  const onNavigation = computed(() => getOnNavigation(eventList.value))
  const contentId = computed(() => cellInfo.contentid ?? '')
  const contentType = computed(() =>
    getContentType(onNavigation.value?.url ?? ''),
  )

  const hasTopTagList = computed(() => topTagList.value.length > 0)
  const hasBrandTagList = computed(() => brandTagList.value.length > 0)
  const hasTitleList = computed(() => titleList.value.length > 0)
  const isThumbnail = computed(() => thumbnail.value !== '')
  const isPlayCta = computed(() => cellInfo.cta === 'icon_autoplay')

  /**
   * goDetail (from cell mixin)
   * @param cellInfo
   */
  const goDetail = (cellInfo = {}) => {
    const eventList = cellInfo?.event_list ?? []
    const onNavigation = getOnNavigation(eventList)
    const onClick = getOnClick(eventList)
    const url = shaveUrl(onNavigation?.url ?? '')
    const toast = onNavigation?.toast_text ?? ''
    const hasAutoPlayCTAButton = onNavigation?.cta === 'icon_autoplay'

    const location = hasAutoPlayCTAButton
      ? changeStreamUrl(url)
      : setGoDetailUrl(url)

    if (!location) {
      return
    }

    if (onClick) {
      sendClickEventLog(onClick)
    }

    if (toast) {
      EventBus.$emit(
        'OPEN_PPV_TOAST',
        () => {
          pushRouter(location)
        },
        toast,
      )
    } else {
      pushRouter(location)
    }
  }

  return {
    ageTag,
    alt,
    altTitle,
    bgColor,
    brandTagList,
    buttonText,
    contentId,
    contentType,
    eventList,
    logoImage,
    logoTitle,
    progress,
    rank,
    ratings,
    targetAge,
    thumbnail,
    titleList,
    topTagList,
    onNavigation,
    isThumbnail,
    isPlayCta,
    hasTopTagList,
    hasBrandTagList,
    hasTitleList,
    goDetail,
  }
}
